import React, { useEffect } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { IOrganizationFormValues } from '../../../../@types/model/masterData/organization/organizationFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import { yupResolver } from '@hookform/resolvers/yup';
import OrganizationModelHelper from '../../../../@types/model/masterData/organization/organizationModelHelper';
import OptionType from '../../../../@types/model/optionType';

interface IOrganizationFormProps {
    isLoading : boolean;
    initialValues : IOrganizationFormValues;
    onSubmit : (values : IOrganizationFormValues) => void;
    onClose : () => void;
}

const OrganizationForm = (props : IOrganizationFormProps) : React.ReactElement => {
    const contactInfos = useAppSelector(x => x.masterData.contactInfos);
    const tumCredentialsList = useAppSelector(x => x.masterData.tumCredentialsList);
    

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Form
     * ==============================================================================================================*/

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IOrganizationFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(OrganizationModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const contactInfoOptions : Array<IOptionType> = useMemo(() => {
        return contactInfos.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [contactInfos]);

    const tumCredentialsListOptions : Array<IOptionType> = useMemo(() => {
        return tumCredentialsList.filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [tumCredentialsList]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    // Determine if waybill fields should be disabled (if they had an initial value and it's an edit)
    const isEditMode = !!props.initialValues.id;
    const waybillPrefixDisabled = isEditMode && !!props.initialValues.waybillPrefix;
    const nextWaybillNumberDisabled = isEditMode && props.initialValues.nextWaybillNumber != null;
    const waybillRangeEndDisabled = isEditMode && props.initialValues.waybillRangeEnd != null;

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20 pt15 pb10'}>
                    <div className={'fdr wfill mb10'}>
                        <ControlledTextField
                            className={'wfill'}
                            name={'code'}
                            label={'Code'}
                            control={control}
                            defaultValue={props.initialValues.code}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledTextField
                            className={'wfill'}
                            name={'name'}
                            label={'Name'}
                            control={control}
                            defaultValue={props.initialValues.name}
                            size={'small'}
                        />
                    </div>
                    <div className={'fdr wfill mb10'}>
                        <ControlledTextField
                            className={'wfill'}
                            name={'exporterCode'}
                            label={'Exporter Code'}
                            control={control}
                            defaultValue={props.initialValues.exporterCode}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledTextField
                            className={'wfill'}
                            name={'tumBusinessId'}
                            label={'TUM Business Id'}
                            control={control}
                            defaultValue={props.initialValues.tumBusinessId?.toString()}
                            size={'small'}
                        />
                    </div>
                    <div className={'fdr wfill mb10'}>
                        <ControlledTextField
                            className={'wfill mt8'}
                            name={'tumLogisticalProviderId'}
                            label={'TUM Logistical Provider Id'}
                            control={control}
                            defaultValue={props.initialValues.tumLogisticalProviderId?.toString()}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledMuiAutoCompleteSelect
                            className={'wfill'}
                            name={'tumCredentials'}
                            label={'TUM Credentials'}
                            control={control}
                            options={tumCredentialsListOptions}
                            defaultValues={props.initialValues.tumCredentials}
                        />
                    </div>
                    <div className={'fdr wfill mb10'}>
                        <ControlledTextField
                            className={'wfill'}
                            name={'waybillPrefix'}
                            label={'Waybill Prefix'}
                            control={control}
                            defaultValue={props.initialValues.waybillPrefix}
                            disabled={waybillPrefixDisabled}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledTextField
                            className={'wfill'}
                            name={'nextWaybillNumber'}
                            label={'Next Waybill Nr'}
                            type={'number'}
                            control={control}
                            defaultValue={props.initialValues.nextWaybillNumber?.toString()}
                            disabled={nextWaybillNumberDisabled}
                            size={'small'}
                        />
                    </div>
                    <div className={'fdr wfill mb10'}>
                        <ControlledTextField
                            className={'wfill mt8'}
                            name={'waybillRangeEnd'}
                            label={'Waybill Range End'}
                            type={'number'}
                            control={control}
                            defaultValue={props.initialValues.waybillRangeEnd?.toString()}
                            disabled={waybillRangeEndDisabled}
                            size={'small'}
                        />
                        <div className={'w20'}/>
                        <ControlledMuiAutoCompleteSelect
                            className={'wfill'}
                            name={'contactInfos'}
                            label={'ContactInfos'}
                            control={control}
                            options={contactInfoOptions}
                            defaultValues={props.initialValues.contactInfos}
                            isMulti
                        />
                    </div>
                    <div className={'fdr flx1 mb10'}>
                        <ControlledLabelledCheckBox
                            className={'flx1'}
                            name={'isSupplier'}
                            label={'Is Supplier'}
                            control={control}
                            defaultValue={props.initialValues.isSupplier}
                        />
                        <div className={'flx1'}/>
                        <ControlledLabelledCheckBox
                            className={'flx1'}
                            name={'isClient'}
                            label={'Is Client'}
                            control={control}
                            defaultValue={props.initialValues.isClient}
                        />
                        <div className={'flx1'}/>
                        <ControlledLabelledCheckBox
                            className={'flx1'}
                            name={'canIntake'}
                            label={'Can Intake'}
                            control={control}
                            defaultValue={props.initialValues.canIntake}
                        />
                        <div className={'flx1'}/>
                        <ControlledLabelledCheckBox
                            className={'flx1'}
                            name={'requiresProject'}
                            label={'Requires Project'}
                            control={control}
                            defaultValue={props.initialValues.requiresProject}
                        />
                        <div className={'flx1'}/>
                    </div>
                    <div className={'fdr flx1 mb5'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default OrganizationForm;
